import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { X, Zap, ChevronRight, ChevronLeft, Users, Trophy, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { tournamentAPI } from '../services/tournamentApi';
import { teammatesApi } from '../../teammates/services/teammatesApi';
import { apiCall } from '../../services/apiUtil';
import { sportTypeAPI } from '../../services/api';
import { btn } from '../../utils/buttonStyles';
import { resolveImageUrl } from '../../profile/hooks/useProfileImage';

const FORMATS = [
  { id: 'round_robin',        label: 'Round Robin',     desc: 'Everyone plays everyone, ranked by points',     minPlayers: 2 },
  { id: 'league_plus_finals', label: 'Round Robin + Final', desc: 'Everyone plays everyone → top 2 play final', minPlayers: 3 },
  { id: 'single_elimination', label: 'Knockout',        desc: 'Lose once, you\'re out',                        minPlayers: 2 },
  { id: 'ipl_style',          label: 'IPL-Style',       desc: 'League stage → top-4 playoffs',                 minPlayers: 4 },
];

const PLAYERS_PER_TEAM_OPTIONS = [1, 2, 3, 4, 5, 6, 11];

function Avatar({ firstName, lastName, profileImage, size = 10, selected }) {
  const initials = `${(firstName || '?')[0]}${(lastName || '')[0] || ''}`.toUpperCase();
  return (
    <div className={`relative w-${size} h-${size} rounded-full overflow-hidden flex-shrink-0 ${selected ? 'ring-2 ring-blue-500 ring-offset-1 ring-offset-gray-900' : ''}`}>
      {profileImage
        ? <img src={resolveImageUrl(profileImage)} alt={firstName} className="w-full h-full object-cover" />
        : <div className="w-full h-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold text-xs">{initials}</div>
      }
      {selected && (
        <div className="absolute inset-0 bg-blue-600/30 flex items-center justify-center">
          <Check className="w-4 h-4 text-white drop-shadow" />
        </div>
      )}
    </div>
  );
}

const POKER_SPORT_NAME = 'Poker';

export default function QuickTournamentModal({ isOpen, onClose, squadId = null, initialSportTypeId = null }) {
  const { theme } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [sportTypes, setSportTypes] = useState([]);
  const [players, setPlayers] = useState([]);   // available pool
  const [loadingPlayers, setLoadingPlayers] = useState(false);

  // Form state
  const [sportTypeId, setSportTypeId] = useState('');
  const [format, setFormat] = useState('round_robin');
  // Single Elimination only — see tournamentScheduleGenerator.js generateSingleElimination
  const [thirdPlace, setThirdPlace] = useState(false);
  const [consolation, setConsolation] = useState('none'); // 'none' | 'first_round'
  // typeOption: 'singles' | 'doubles' | 'teams'
  const [typeOption, setTypeOption] = useState('teams');
  const [playersPerTeam, setPlayersPerTeam] = useState(2);
  const [selectedIds, setSelectedIds] = useState([]);
  const [tournamentName, setTournamentName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  // Derive participantsType + playersPerTeam from typeOption
  const participantsType = typeOption === 'singles' ? 'singles' : 'teams';
  const effectivePlayersPerTeam = typeOption === 'singles' ? 1 : typeOption === 'doubles' ? 2 : playersPerTeam;

  // Reset when opened; pre-fill sport from squad context
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setSelectedIds([]);
      setTournamentName('');
      setFormat('round_robin');
      setThirdPlace(false);
      setConsolation('none');
      setSportTypeId(initialSportTypeId ? String(initialSportTypeId) : '');
      setTypeOption('teams');
      setPlayersPerTeam(2);
    }
  }, [isOpen, initialSportTypeId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load sport types (cached — see sportTypeAPI.getAll)
  useEffect(() => {
    if (!isOpen) return;
    sportTypeAPI.getAll().then(data => {
      if (Array.isArray(data)) setSportTypes(data);
    }).catch(() => {});
  }, [isOpen]);

  // Load player pool
  useEffect(() => {
    if (!isOpen) return;
    setLoadingPlayers(true);
    const fetch = squadId
      ? apiCall(`/groups/${squadId}/members?page=1&limit=50`)
      : teammatesApi.getTeammates(1, 100);

    fetch
      .then(res => {
        const raw = res?.data ?? res;
        const list = Array.isArray(raw) ? raw : (raw?.members ?? raw?.teammates ?? []);
        const normalized = list.map(item => {
          const u = item.user || item;
          const rawId = item.userId ?? u.id;
          return {
            id:           String(rawId),
            firstName:    u.firstName || u.first_name || '',
            lastName:     u.lastName  || u.last_name  || '',
            profileImage: u.profileImage || u.profilePicture || u.profile_image || null,
          };
        }).filter(u => u.id);
        setPlayers(normalized);
      })
      .catch(() => {})
      .finally(() => setLoadingPlayers(false));
  }, [isOpen, squadId, user?.id]);

  // Which type options are available for the selected sport
  const availableTypes = useMemo(() => {
    const sport = sportTypes.find(s => String(s.id) === String(sportTypeId));
    if (!sport || !sportTypeId) return ['singles', 'doubles', 'teams'];
    if (sport.maxPlayersPerSide === 2) return ['singles', 'doubles'];
    return ['teams'];
  }, [sportTypeId, sportTypes]);

  const TYPE_LABELS = {
    singles: { label: 'Singles', desc: '1 player per side' },
    doubles: { label: 'Doubles', desc: '2 players per side' },
    teams:   { label: 'Teams',   desc: 'Custom team size' },
  };

  // Keep typeOption valid whenever availableTypes or typeOption changes
  useEffect(() => {
    if (!availableTypes.includes(typeOption)) {
      setTypeOption(availableTypes[0]);
    }
  }, [availableTypes, typeOption]); // eslint-disable-line react-hooks/exhaustive-deps

  const teamsNeeded = typeOption === 'singles' ? selectedIds.length : Math.floor(selectedIds.length / effectivePlayersPerTeam);
  const selectedFormat = FORMATS.find(f => f.id === format);
  const minPlayersNeeded = typeOption === 'singles'
    ? (selectedFormat?.minPlayers ?? 2)
    : (selectedFormat?.minPlayers ?? 2) * effectivePlayersPerTeam;
  const isValidSelection = typeOption === 'singles'
    ? selectedIds.length >= minPlayersNeeded
    : selectedIds.length >= minPlayersNeeded && selectedIds.length % effectivePlayersPerTeam === 0;

  const togglePlayer = useCallback((id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }, []);

  // Build preview teams for step 3
  const previewTeams = useCallback(() => {
    if (typeOption === 'singles') {
      return selectedIds.map(id => {
        const p = players.find(pl => pl.id === id);
        return { name: `${p?.firstName || ''} ${p?.lastName || ''}`.trim(), members: [p] };
      });
    }
    const shuffled = [...selectedIds];
    const teams = [];
    for (let i = 0; i < shuffled.length; i += effectivePlayersPerTeam) {
      const chunk = shuffled.slice(i, i + effectivePlayersPerTeam);
      const members = chunk.map(id => players.find(p => p.id === id));
      let name;
      if (effectivePlayersPerTeam === 2) {
        name = members.map(m => m?.firstName || 'Player').join('/');
      } else {
        name = `Team ${teams.length + 1}`;
      }
      teams.push({ name, members });
    }
    return teams;
  }, [selectedIds, players, typeOption, effectivePlayersPerTeam]);

  async function handleCreate() {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const result = await tournamentAPI.quickCreate({
        sportTypeId:    sportTypeId ? parseInt(sportTypeId) : undefined,
        format,
        participantsType,
        playersPerTeam: typeOption === 'singles' ? undefined : effectivePlayersPerTeam,
        playerIds:      selectedIds,
        tournamentName: tournamentName.trim() || undefined,
        ownerType:      squadId ? 'group' : undefined,
        ownerId:        squadId ? String(squadId) : undefined,
        // Single Elimination only — quick-create generates the schedule in
        // the same call (see createQuickTournament), so this rides along in
        // the same request rather than a separate generate-schedule call.
        ...(format === 'single_elimination' ? { config: { thirdPlace, consolation } } : {}),
      });
      toast.success('Tournament created! Let\'s play!');
      onClose();
      navigate(`/tournament/${result.tournamentId}?tab=schedule`);
    } catch (err) {
      toast.error(err?.message || 'Failed to create tournament');
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  if (!isOpen) return null;

  const autoName = (() => {
    const d = new Date();
    return `Quick Tournament — ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  })();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative w-full max-w-lg rounded-2xl shadow-2xl ${theme.card} border flex flex-col max-h-[90vh]`}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-700/50 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-600/20 border border-blue-500/30 flex items-center justify-center">
              <Zap className="w-4 h-4 text-blue-400" />
            </div>
            <div>
              <h2 className={`font-bold text-base ${theme.text}`}>Quick Tournament</h2>
              <p className={`text-xs ${theme.textSecondary}`}>Step {step} of 3</p>
            </div>
          </div>
          <button onClick={onClose} className={`${btn.icon} ${theme.cardHover}`}>
            <X className={`w-4 h-4 ${theme.textSecondary}`} />
          </button>
        </div>

        {/* Step indicators */}
        <div className="flex gap-1.5 px-6 pt-4 flex-shrink-0">
          {[1, 2, 3].map(s => (
            <div key={s} className={`h-1 flex-1 rounded-full transition-colors ${s <= step ? 'bg-blue-500' : 'bg-gray-700'}`} />
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">

          {/* ── Step 1: Setup ── */}
          {step === 1 && (
            <div className="space-y-5">
              <div>
                <label className={`block text-xs font-medium ${theme.textSecondary} mb-1.5`}>
                  Sport {initialSportTypeId && <span className="text-xs text-blue-400 ml-1">(from squad)</span>}
                </label>
                <select
                  value={sportTypeId}
                  onChange={e => {
                    const newId = e.target.value;
                    setSportTypeId(newId);
                    const sport = sportTypes.find(s => String(s.id) === String(newId));
                    if (!sport || !newId) {
                      setTypeOption('teams');
                    } else if (sport.maxPlayersPerSide === 2) {
                      setTypeOption('singles');
                    } else {
                      setTypeOption('teams');
                    }
                  }}
                  disabled={!!initialSportTypeId}
                  className={`w-full px-3 py-2 border rounded-lg text-sm ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent ${initialSportTypeId ? 'opacity-60 cursor-not-allowed' : ''}`}
                >
                  <option value="">Any sport</option>
                  {sportTypes
                    .filter(s => s.sportName !== POKER_SPORT_NAME)
                    .map(s => (
                      <option key={s.id} value={s.id}>{s.sportName}</option>
                    ))
                  }
                </select>
              </div>

              <div>
                <label className={`block text-xs font-medium ${theme.textSecondary} mb-1.5`}>Format</label>
                <div className="grid grid-cols-2 gap-2">
                  {FORMATS.map(f => (
                    <button
                      key={f.id}
                      onClick={() => setFormat(f.id)}
                      className={`p-3 rounded-xl border text-left transition-all ${
                        format === f.id
                          ? 'border-blue-500 bg-blue-600/10'
                          : `border-gray-700 ${theme.cardHover}`
                      }`}
                    >
                      <p className={`text-sm font-medium ${format === f.id ? 'text-blue-400' : theme.text}`}>{f.label}</p>
                      <p className={`text-xs mt-0.5 ${theme.textSecondary}`}>{f.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              {format === 'single_elimination' && (
                <div className="space-y-2">
                  <label className={`flex items-center gap-2 text-xs font-medium ${theme.text} cursor-pointer`}>
                    <input
                      type="checkbox"
                      checked={thirdPlace}
                      onChange={e => setThirdPlace(e.target.checked)}
                      className="rounded"
                    />
                    Include third-place playoff
                  </label>
                  <label className={`flex items-center gap-2 text-xs font-medium ${theme.text} cursor-pointer`}>
                    <input
                      type="checkbox"
                      checked={consolation === 'first_round'}
                      onChange={e => setConsolation(e.target.checked ? 'first_round' : 'none')}
                      className="rounded"
                    />
                    Include consolation bracket
                  </label>
                </div>
              )}

              <div>
                <label className={`block text-xs font-medium ${theme.textSecondary} mb-1.5`}>Type</label>
                <div className="flex gap-2">
                  {availableTypes.map(t => (
                    <button
                      key={t}
                      onClick={() => setTypeOption(t)}
                      className={`flex-1 py-2.5 rounded-lg border text-sm font-medium transition-all ${
                        typeOption === t
                          ? 'border-blue-500 bg-blue-600/10 text-blue-400'
                          : `border-gray-700 ${theme.textSecondary} ${theme.cardHover}`
                      }`}
                    >
                      <div>{TYPE_LABELS[t].label}</div>
                      <div className="text-xs font-normal opacity-70 mt-0.5">{TYPE_LABELS[t].desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {typeOption === 'teams' && (
                <div>
                  <label className={`block text-xs font-medium ${theme.textSecondary} mb-1.5`}>Players per team</label>
                  <div className="flex flex-wrap gap-2">
                    {PLAYERS_PER_TEAM_OPTIONS.filter(n => n > 2).map(n => (
                      <button
                        key={n}
                        onClick={() => setPlayersPerTeam(n)}
                        className={`w-10 h-9 rounded-lg border text-sm font-medium transition-all ${
                          playersPerTeam === n
                            ? 'border-blue-500 bg-blue-600/10 text-blue-400'
                            : `border-gray-700 ${theme.textSecondary} ${theme.cardHover}`
                        }`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Step 2: Pick Players ── */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <p className={`text-sm ${theme.textSecondary}`}>
                    {squadId ? 'Squad members' : 'Teammates'}
                  </p>
                  {players.length > 0 && (
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => setSelectedIds(players.map(p => p.id))}
                        className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
                      >
                        Select all
                      </button>
                      <span className={`text-xs ${theme.textSecondary}`}>·</span>
                      <button
                        onClick={() => setSelectedIds([])}
                        className={`text-xs ${theme.textSecondary} hover:text-white transition-colors`}
                      >
                        Clear
                      </button>
                    </div>
                  )}
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  isValidSelection ? 'bg-green-600/20 text-green-400' : 'bg-gray-700 text-gray-400'
                }`}>
                  {selectedIds.length} selected
                  {typeOption !== 'singles' && selectedIds.length > 0 && (
                    <> · {teamsNeeded} {typeOption === 'doubles' ? 'pair' : 'team'}{teamsNeeded !== 1 ? 's' : ''}</>
                  )}
                </span>
              </div>

              {typeOption !== 'singles' && (
                isValidSelection && selectedIds.length > 0 ? (
                  <p className="text-xs text-green-400 bg-green-600/10 border border-green-500/20 rounded-lg px-3 py-2">
                    ✓ {teamsNeeded} {typeOption === 'doubles' ? 'pair' : 'team'}{teamsNeeded !== 1 ? 's' : ''} ready — tap Next to preview.
                  </p>
                ) : (
                  <p className="text-xs text-gray-400 bg-gray-800/60 border border-gray-700 rounded-lg px-3 py-2">
                    {selectedIds.length === 0
                      ? `Select players in groups of ${effectivePlayersPerTeam} to form ${typeOption === 'doubles' ? 'pairs' : 'teams'}.`
                      : `Need ${effectivePlayersPerTeam - (selectedIds.length % effectivePlayersPerTeam)} more player${effectivePlayersPerTeam - (selectedIds.length % effectivePlayersPerTeam) !== 1 ? 's' : ''} to complete the last ${typeOption === 'doubles' ? 'pair' : 'team'}.`
                    }
                  </p>
                )
              )}

              {loadingPlayers ? (
                <div className="flex justify-center py-8">
                  <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : players.length === 0 ? (
                <div className={`text-center py-10 ${theme.textSecondary} text-sm`}>
                  <Users className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  <p>No {squadId ? 'squad members' : 'teammates'} found.</p>
                </div>
              ) : (
                <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
                  {players.map(p => {
                    const isSelected = selectedIds.includes(p.id);
                    return (
                      <button
                        key={p.id}
                        onClick={() => togglePlayer(p.id)}
                        className={`w-full flex items-center gap-3 p-2.5 rounded-xl border transition-all text-left ${
                          isSelected
                            ? 'border-blue-500 bg-blue-600/10'
                            : `border-transparent ${theme.cardHover}`
                        }`}
                      >
                        <Avatar
                          firstName={p.firstName}
                          lastName={p.lastName}
                          profileImage={p.profileImage}
                          size={9}
                          selected={isSelected}
                        />
                        <span className={`text-sm font-medium ${theme.text}`}>
                          {p.firstName} {p.lastName}
                        </span>
                        {isSelected && <Check className="w-4 h-4 text-blue-400 ml-auto" />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── Step 3: Preview & Launch ── */}
          {step === 3 && (
            <div className="space-y-4">
              <div>
                <label className={`block text-xs font-medium ${theme.textSecondary} mb-1.5`}>Tournament name</label>
                <input
                  type="text"
                  value={tournamentName}
                  onChange={e => setTournamentName(e.target.value)}
                  placeholder={autoName}
                  className={`w-full rounded-lg border px-3 py-2 text-sm ${theme.input} focus:outline-none focus:ring-1 focus:ring-blue-500`}
                />
              </div>

              <div>
                <p className={`text-xs font-medium ${theme.textSecondary} mb-2`}>
                  {typeOption === 'singles' ? 'Players' : typeOption === 'doubles' ? 'Pairs' : 'Teams'} ({previewTeams().length})
                </p>
                <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                  {previewTeams().map((team, i) => (
                    <div key={i} className={`flex items-center gap-3 p-2.5 rounded-xl border border-gray-700 ${theme.card}`}>
                      <div className="flex -gap-x-2">
                        {team.members.slice(0, 4).map((m, j) => (
                          <Avatar key={j} firstName={m?.firstName} lastName={m?.lastName} profileImage={m?.profileImage} size={7} />
                        ))}
                      </div>
                      <span className={`text-sm font-medium ${theme.text}`}>{team.name}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className={`rounded-xl border border-gray-700/50 p-3 space-y-1.5`}>
                <Row label="Format" value={selectedFormat?.label} theme={theme} />
                <Row label="Sport"  value={sportTypes.find(s => String(s.id) === String(sportTypeId))?.sportName || 'Any'} theme={theme} />
                <Row label="Schedule will start immediately" value={null} theme={theme} />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-700/50 flex-shrink-0">
          <button
            onClick={() => step > 1 ? setStep(s => s - 1) : onClose()}
            className={`${btn.base} ${btn.md} ${theme.buttonGhost || 'text-gray-400 hover:text-white'} flex items-center gap-1.5`}
          >
            <ChevronLeft className="w-4 h-4" />
            {step === 1 ? 'Cancel' : 'Back'}
          </button>

          {step < 3 ? (
            <button
              onClick={() => setStep(s => s + 1)}
              disabled={step === 2 && !isValidSelection}
              className={`${btn.base} ${btn.md} ${theme.buttonPrimary} flex items-center gap-1.5 disabled:opacity-40`}
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={handleCreate}
              disabled={submitting}
              className={`${btn.base} ${btn.md} ${theme.buttonPrimary} flex items-center gap-2 disabled:opacity-40`}
            >
              {submitting ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Zap className="w-4 h-4" />
              )}
              {submitting ? 'Creating…' : 'Start Tournament'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, theme }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className={theme.textSecondary}>{label}</span>
      {value && <span className={`font-medium ${theme.text}`}>{value}</span>}
    </div>
  );
}
